import { reaction } from 'mobx';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

interface SyncOptions<TStore, TState> {
	store: TStore;
	getter: (store: TStore) => TState;
	setter: (store: TStore, newState: TState) => void;
	serialize: (state: TState) => Record<string, string>;
	deserialize: (params: Record<string, string>) => TState;
}

export function useSyncStoreWithSearchParams<
	TStore,
	TState extends Record<string, unknown>,
>({
	store,
	getter,
	setter,
	serialize,
	deserialize,
}: SyncOptions<TStore, TState>) {
	const [searchParams, setSearchParams] = useSearchParams();

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		const paramsObject: Record<string, string> = {};
		searchParams.forEach((value, key) => {
			paramsObject[key] = value;
		});

		const newState = deserialize(paramsObject);
		const currentState = getter(store);

		if (JSON.stringify(newState) !== JSON.stringify(currentState)) {
			setter(store, newState);
		}
	}, [searchParams, store, getter, setter, deserialize, serialize]);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		const dispose = reaction(
			() => getter(store),
			(storeState) => {
				const serialized = serialize(storeState);

				setSearchParams((current: URLSearchParams) => {
					const newParams = new URLSearchParams(current);
					Object.entries(serialized).forEach(([key, value]) => {
						if (value !== null && value !== '') {
							newParams.set(key, value);
						}
					});
					return newParams;
				});
			}
		);

		return dispose;
	}, [store, getter, serialize, setSearchParams]);
}
